import { useState } from 'react';
import { Box, IconButton } from '@material-ui/core';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import InfoRoundedIcon from '@material-ui/icons/InfoRounded';

import { SButton, SText } from '@setvi/shared/components';

const WARNING_ACCENT = '#FF9F29';
const FAILED_COLOR = '#FA2020';
const SUCCESS_COLOR = '#23A566';

export interface ExportFileRow {
  name: string;
  failed: boolean;
}

interface ExportWarningDialogProps {
  files: ExportFileRow[];
  hasCompletedFiles: boolean;
  // includeFailed = true -> export all files; false -> completed only
  onExport: (includeFailed: boolean) => Promise<void>;
  onClose: () => void;
}

const ExportWarningDialog = ({
  files,
  hasCompletedFiles,
  onExport,
  onClose
}: ExportWarningDialogProps) => {
  const [loading, setLoading] = useState<null | 'completed' | 'all'>(null);
  const busy = loading !== null;

  const failedCount = files.filter(f => f.failed).length;
  // Failed files first, then completed ones.
  const sortedFiles = [...files].sort(
    (a, b) => Number(b.failed) - Number(a.failed)
  );

  const handleClick = async (includeFailed: boolean) => {
    setLoading(includeFailed ? 'all' : 'completed');
    try {
      await onExport(includeFailed);
    } finally {
      setLoading(null);
    }
  };

  return (
    <Box style={{ width: 460, padding: 24, boxSizing: 'border-box' }}>
      <Box
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12
        }}>
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            minWidth: 0
          }}>
          <Box
            style={{
              width: 40,
              height: 40,
              minWidth: 40,
              borderRadius: '50%',
              backgroundColor: WARNING_ACCENT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            <WarningRoundedIcon htmlColor="#fff" style={{ fontSize: 22 }} />
          </Box>
          <SText
            size="lg"
            weight="bold"
            fontColor="#20262D"
            noLineClamp
            title="Some files failed to process"
          />
        </Box>

        <IconButton size="small" onClick={onClose} disabled={busy}>
          <CloseRoundedIcon htmlColor="#5D7083" style={{ fontSize: 22 }} />
        </IconButton>
      </Box>

      <Box style={{ marginTop: 12 }}>
        <SText
          size="sm"
          fontColor="#20262D"
          noLineClamp
          title={`${failedCount} of ${files.length} files failed to process. Choose what to include in the export.`}
        />
      </Box>

      {files.length > 0 && (
        <Box
          style={{
            marginTop: 16,
            borderRadius: 12,
            border: '1px solid #ECEEF1',
            maxHeight: 180,
            overflow: 'auto'
          }}>
          {sortedFiles.map((file, index) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={`${file.name}-${index}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                borderTop: index === 0 ? 'none' : '1px solid #F2F3F5'
              }}>
              <Box style={{ flex: 1, minWidth: 0 }}>
                <SText
                  size="sm"
                  weight="medium"
                  fontColor={file.failed ? FAILED_COLOR : '#20262D'}
                  title={file.name}
                />
              </Box>
              <Box
                style={{
                  flexShrink: 0,
                  padding: '2px 10px',
                  borderRadius: 999,
                  backgroundColor: file.failed ? '#FDECEC' : '#E7F6EF'
                }}>
                <SText
                  size="xs"
                  weight="semibold"
                  fontColor={file.failed ? FAILED_COLOR : SUCCESS_COLOR}
                  title={file.failed ? 'Failed' : 'Completed'}
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Box
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          marginTop: 16,
          padding: 12,
          borderRadius: 12,
          backgroundColor: '#EBF5FF'
        }}>
        <InfoRoundedIcon
          htmlColor="#1E96FC"
          style={{ fontSize: 18, minWidth: 18, marginTop: 1 }}
        />
        <SText
          size="sm"
          fontColor="#01294B"
          noLineClamp
          title="Failed files won't include all of their items, and the items they do contain may be out of order."
        />
      </Box>

      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginTop: 24
        }}>
        <SButton
          fullWidth
          variant="outlined"
          loading={loading === 'all'}
          disabled={busy}
          onClick={() => handleClick(true)}
          style={{ borderRadius: 36, padding: '10px 24px', fontWeight: 600 }}>
          Export all
        </SButton>
        <SButton
          fullWidth
          variant="contained"
          loading={loading === 'completed'}
          disabled={busy || !hasCompletedFiles}
          onClick={() => handleClick(false)}
          tooltipTitle={
            !hasCompletedFiles ? 'There are no completed files to export' : ''
          }
          style={{ borderRadius: 36, padding: '10px 24px', fontWeight: 600 }}>
          Export completed only
        </SButton>
      </Box>
    </Box>
  );
};

export default ExportWarningDialog;
